import { z } from 'zod';

export const dimensionSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]*$/, 'snake_case letters/numbers/underscores'),
  name: z.string().min(1, 'Required'),
  description: z.string().optional(),
  weight: z.number().int().min(0).max(100),
  anchors: z.object({
    low: z.string().min(1, 'Required'),
    mid: z.string().min(1, 'Required'),
    high: z.string().min(1, 'Required'),
  }),
});

export const bandSchema = z.object({
  label: z.string().min(1, 'Required'),
  min: z.number().min(1).max(5),
});

export const FLAG_CATEGORIES = ['NEGATIVE', 'POSITIVE', 'INFORMATIONAL'] as const;

export const flagSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]*$/, 'snake_case letters/numbers/underscores'),
  name: z.string().min(1, 'Required'),
  category: z.enum(FLAG_CATEGORIES),
  guidance: z.string().min(1, 'Required'),
  note: z.string().optional(),
});

export const rubricFormSchema = z
  .object({
    name: z.string().min(1, 'Required'),
    description: z.string(),
    dimensions: z.array(dimensionSchema).min(1).max(20),
    bands: z.array(bandSchema).min(2).max(10),
    flags: z.array(flagSchema).max(10),
  })
  .refine((v) => v.dimensions.reduce((s, d) => s + d.weight, 0) === 100, {
    message: 'Weights must sum to 100',
    path: ['dimensions'],
  })
  .refine((v) => new Set(v.dimensions.map((d) => d.key)).size === v.dimensions.length, {
    message: 'Dimension keys must be unique',
    path: ['dimensions'],
  })
  .refine((v) => v.bands.every((b, i) => i === 0 || b.min < v.bands[i - 1].min), {
    message: 'Bands must be ordered high to low',
    path: ['bands'],
  })
  .refine((v) => v.bands.length > 0 && v.bands[v.bands.length - 1].min === 1, {
    message: 'The lowest band must start at 1',
    path: ['bands'],
  })
  .refine((v) => new Set(v.flags.map((f) => f.key)).size === v.flags.length, {
    message: 'Flag keys must be unique',
    path: ['flags'],
  });

export type RubricFormValues = z.infer<typeof rubricFormSchema>;
